package one.healthium.today.ui.fodmap

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import androidx.lifecycle.compose.LifecycleResumeEffect
import ifaandroid.feature.today_v2.today_ui_fodmap.generated.resources.Res
import ifaandroid.feature.today_v2.today_ui_fodmap.generated.resources.bg_calendar
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format.char
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import one.healthium.base.utils.endOfWeek
import one.healthium.base.utils.localizedDayOfWeekNames
import one.healthium.base.utils.localizedMonthNames
import one.healthium.base.utils.startOfWeek
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.effect.greetingMessage
import org.jetbrains.compose.resources.painterResource
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes

data class TodayCalendarUiModel(
    val userName: String,
)

@Composable
fun TodayCalendar(uiModel: TodayCalendarUiModel) {
    var now by remember { mutableStateOf(Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())) }
    val scope = rememberCoroutineScope()

    LifecycleResumeEffect(Unit) {
        val job = scope.launch {
            while (isActive) {
                now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
                delay(1.minutes.inWholeMilliseconds)
            }
        }
        onPauseOrDispose {
            job.cancel()
        }
    }
    val today = now.date

    val currentWeek = remember(uiModel) {
        val startOfWeek = today.startOfWeek()
        val endOfWeek = today.endOfWeek()
        buildList {
            var current = startOfWeek
            while (current <= endOfWeek) {
                add(current)
                current = current.plus(DatePeriod(days = 1))
            }
        }
    }

    Box {
        Image(
            modifier = Modifier.fillMaxWidth(),
            painter = painterResource(Res.drawable.bg_calendar),
            contentScale = ContentScale.FillBounds,
            contentDescription = null,
        )
        Column(
            modifier = Modifier
                .padding(horizontal = 24.dp)
                .statusBarsPadding(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Spacer(Modifier.height(20.dp))
            Text(
                text = getGreetingMessage(uiModel.userName),
                style = AppTheme.textStyles.poppins18SemiBold,
                color = AppTheme.colors.onPrimary.copy(alpha = 0.75f),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Spacer(Modifier.height(8.dp))
            Text(
                text = TodayFormat().format(today),
                style = AppTheme.textStyles.poppins32SemiBold,
                color = AppTheme.colors.onPrimary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Spacer(Modifier.height(24.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(330.dp),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                currentWeek.fastForEach { date ->
                    DayItem(
                        date = date,
                        today = today,
                    )
                }
            }
        }
    }
}

@Composable
private fun DayItem(
    date: LocalDate,
    today: LocalDate,
) {
    val highlighted = date == today
    val dayColor = when {
        highlighted -> AppTheme.colors.primary
        date < today -> AppTheme.colors.onPrimary.copy(alpha = 0.5f)
        else -> AppTheme.colors.onPrimary
    }
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = DayOfWeekFormat().format(date),
            style = AppTheme.textStyles.tertiary14_700,
            color = AppTheme.colors.onPrimary.copy(alpha = 0.75f),
        )
        Spacer(Modifier.height(10.dp))
        Box(
            modifier = Modifier
                .then(
                    if (highlighted) Modifier.background(AppTheme.colors.secondary, CircleShape)
                    else Modifier
                )
                .sizeIn(minWidth = 30.dp, minHeight = 30.dp),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                modifier = Modifier.padding(top = 2.dp),
                text = DayOfMonthFormat.format(date),
                style = AppTheme.textStyles.tertiary18_400,
                color = dayColor,
            )
        }
    }
}

@Composable
private fun getGreetingMessage(userName: String): String {
    val greeting = greetingMessage()
    if (userName.isEmpty()) return greeting
    return "$greeting, $userName"
}

private fun DayOfWeekFormat() = LocalDate.Format {
    dayOfWeek(localizedDayOfWeekNames(abbreviated = true))
}

private val DayOfMonthFormat = LocalDate.Format {
    day()
}

private fun TodayFormat() = LocalDate.Format {
    dayOfWeek(localizedDayOfWeekNames())
    char(' ')
    day()
    char(' ')
    monthName(localizedMonthNames())
}